import { baseQueryWithReauth } from './baseQuery';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { PUERTO_MALVINAS } from '../commons/States';
import { IBookingGirosResponse, IEstadisticasBookingGirosResponse, IPayloadBookingGiros } from '../pages/Cruceros/TemporadasReservas/AgendaBuquesGiros/types';

export interface TipoCarga {
    id: number;
    sigla: string;
    nombre: string;
    unidadMedida: string;
    activo?: boolean;
}

// AE4-663 — Respuesta del pre-check de calado al aceptar giro directo
export type ValidarCaladoGiroResponse = {
    requiereAlertaCalado: boolean;
    cantidadBuquesSolapados: number;
};

// AE4-433 — Alerta por demoras de egreso reiteradas
export type AlertaDemoraEgreso = {
    cantidadDemoras: number;
    umbral: number;
    superaUmbral: boolean;
} | null;

// AE4-711 — Reemplazo de buque en giro con reserva paga (Art. 11)
export interface BuqueDisponibleReemplazo {
    id: number;
    nombre: string;
    imo: string;
    matricula: string;
    estado: 'AP' | 'SB';
    esloraMaxima: number;
    trb: number;
    trn: number;
    tipoBuque: { id: number; nombre: string };
}

export interface ValoresReferenciaGiro {
    esloraMaxima: number;
    trb: number;
    trn: number;
}

export interface ReemplazarBuqueResponse {
    idGiro: string;
    estado: string;
    buque: Record<string, any>;
    buqueAnterior: Record<string, any>;
    valoresReferencia: ValoresReferenciaGiro;
    alertas: string[];
}

export interface GiroDetalle {
    alertaDemoraEgreso: AlertaDemoraEgreso;
    // AE4-553 — Alerta de disponibilidad de eslora (acción de ruptura, solo USH)
    alertaRuptura?: string | null;
    // AE4-711 — Alertas y valores de referencia de reemplazo de buque (Art. 11). El campo se llama
    // alertasReemplazo (no alertas) para no confundirse con alertaRuptura/alertaDemoraEgreso, que
    // conviven en el mismo nivel de la respuesta — confirmado en ACCIONES-FRONT-AMBIGUEDADES.md §4.
    alertasReemplazo: string[];
    valoresReferencia: ValoresReferenciaGiro | null;
    // AE4-577 — Espera de respuesta de agencia tras desplazamiento a RADA por ruptura
    fechaLimiteRespuestaRuptura?: string | null;
    idEstadoGiroPrevioRuptura?: string | null;
    // AE4-432 — Campos de inactividad operativa (Art. 9)
    fechaInactivoDesde?: string | null;
    fechaInactivoHasta?: string | null;
    // AE4-636 — Recargo Art. 6°: buque sin equipo contra incendios
    buqueSinEquipoIncendios?: boolean;
    // AE4-639 — Recargo Art. 15°: incumplimiento de giro a muelle
    incumplimientoGiroMuelle?: boolean;
    // AE4-637 — Recargo Art. 8°: reparación mayor
    reparacionMayor?: boolean;
    // AE4-731 — Snapshot de subagencia en el detalle del giro (Back-Gestion-Giros, AE4-738)
    buque?: {
        subagencia?: { id: number; nombre: string } | null;
        [key: string]: any;
    };
    [key: string]: any;
}

export interface AltaGiroResponse {
    idGiro: string;
    uuidConversacion?: string;
    evento?: string;
    mensaje?: string;
    disponibilidadZona?: string;
}

// AE4-432 — Body tipado de PATCH /giros/:id/linea-vanguardia
export interface UpdateTripDataBody {
    esEntradaPuerto?: boolean;
    idTipoTrafico?: number | string | null;
    idTipoOperacion?: number | string | null;
    fechaEntradaVanguardia?: string | null;
    fechaSalidaVanguardia?: string | null;
    buqueInactivo?: boolean;
    // AE4-636 — Recargo Art. 6°: buque sin equipo contra incendios
    buqueSinEquipoIncendios?: boolean;
    // AE4-639 — Recargo Art. 15°: incumplimiento de giro a muelle
    incumplimientoGiroMuelle?: boolean;
    // AE4-637 — Recargo Art. 8°: reparación mayor
    reparacionMayor?: boolean;
    notaOperacion?: string;
    idAgenciaFacturacion?: number | string | null;
    fechaInactivoDesde?: string | null;
    fechaInactivoHasta?: string | null;
}

// AE4-409 — Penalidades por cancelación/modificación de giro aprobado (Art. 13 y Art. 14)
export type TramoPenalidad = 'a' | 'b' | 'c';

export interface PreviewPenalidadCancelacion {
    tramo: TramoPenalidad;
    porcentajePenalidad: 0 | 25 | 75;
    anticipoPerdido: number;
    importePenalidad: number;
    cargoFijo300: number;
    total: number;
    importeUsoPuerto: number;
}

export interface CancelarGiroPenalidadResponse {
    idLiquidacionPenalidad: string;
    tramo: TramoPenalidad;
    total: number;
}

export interface EximirPenalidadPayload {
    porcentajeExencion: 0 | 50 | 100;
    eximeCargo300: boolean;
    motivo: string;
    rutaDocumentacion: string;
}

export interface EximirPenalidadResponse {
    idEximicion: string;
    importeAjustado: number;
}

// AE4-653 — Recargo diferido por edición de movimiento por agencia (Art. 14°)
export interface CamposModificadosAgencia {
    [campo: string]: { de: any; a: any };
}

export interface ModificacionAgencia {
    id: string;
    idGiro: string;
    idOrganizacion: number;
    fecha: string;
    idUsuario: number;
    editorRol: 'AGENCIA' | 'OPERADOR';
    motivo: string;
    camposModificados: CamposModificadosAgencia;
    aplicaCargo300: boolean;
    excepcionCargo300: boolean;
    motivoExcepcion: string | null;
    idUsuarioExcepcion: number | null;
}

export interface ModificacionesAgenciaResponse {
    modificaciones: ModificacionAgencia[];
    contadorAgencia: number;
}

export interface ExceptuarModificacionPayload {
    idGiro: string;
    idModificacion: string;
    punible: boolean;
    motivo: string;
}

// AE4-710 — Cambio de agencia marítima en el trámite de giro
export interface CambiarAgenciaGiroPayload {
    idGiro: string;
    idEmpresaNueva: number;
}

export interface CambiarAgenciaGiroResponse {
    idGiro: string;
    estado: string;
    agencia: { id: number; nombre: string };
    agenciaAnterior: { id: number; nombre: string };
}

export interface AgenciaDisponibleGiro {
    id: number;
    nombre: string;
    relacion: 'titular' | 'subagencia';
}

export const girosApi = createApi({
    reducerPath: 'giros',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Giros', 'LiquidacionTasas', 'ModificacionesAgencia'],
    endpoints: (builder) => ({
        getTerminals: builder.query<any, void>({
            query: () => ({ url: 'giros/terminales' }),
            transformResponse: (response: { data: { terminales: any } }) =>
                response?.data?.terminales
        }),
        getPorts: builder.query<any, void>({
            query: () => ({
                url: 'giros/puertos'
            }),
            transformResponse: (response: { data: { puertos: any } }) => {
                const index = response?.data?.puertos?.findIndex((i: any) => i.id === 13956);
                let item = response?.data?.puertos?.find((i: any) => i.id === 13956);
                let newItem = {
                    ...item,
                    nombre: PUERTO_MALVINAS
                };
                const auxData = [...response?.data?.puertos];
                auxData?.splice(index, 1, newItem);
                return auxData;
            }
        }),
        validateMovs: builder.mutation({
            query: (data) => ({
                url: 'giros/validar-datos',
                body: data,
                method: 'POST'
            })
        }),
        getAssemblersAndTenants: builder.query<any, void>({
            query: () => ({
                url: 'giros/armadores-y-locatarios'
            }),
            transformResponse: (response: { data: { empresas: any } }) => response?.data?.empresas
        }),
        getChargeTypes: builder.query<TipoCarga[], void>({
            query: () => ({
                url: 'giros/tipos-carga'
            }),
            transformResponse: (response: { data: { tiposCarga: TipoCarga[] } }) =>
                response?.data?.tiposCarga
        }),
        getGiros: builder.query({
            query: ({ filters }) => ({
                url: '/giros',
                params: filters
            }),
            providesTags: ['Giros']
        }),
        getBlkData: builder.query({
            query: (params) => ({
                url: '/blockchain',
                params
            }),
            providesTags: ['Giros']
        }),
        validateBlkData: builder.mutation({
            query: ({ body, idGiro }) => ({
                url: `giros/${idGiro}/validar-datos-blockchain`,
                method: 'POST',
                body
            })
        }),
        dowloadBlkPdf: builder.query({
            query: (txId) => ({
                url: `blockchain/pdf/${txId}`
            }),
            transformResponse: (response: { data: { pdf: any } }) => response?.data?.pdf
        }),
        addNewdocking: builder.mutation({
            query: (data) => ({
                url: 'giros',
                method: 'POST',
                body: data
            })
        }),
        dockingUploadFiles: builder.mutation({
            query: ({ idGiro, file }) => ({
                url: `giros/${idGiro}/adjunto`,
                body: file,
                method: 'POST'
            })
        }),
        historyConversation: builder.query<any, void>({
            query: (idGiro) => `giros/${idGiro}/conversacion`,
            transformResponse: (response: { data: { historial: any } }) => response?.data?.historial
        }),
        getGiroById: builder.query<GiroDetalle, any>({
            query: (idGiro) => ({
                url: `giros/${idGiro}`
            }),
            // alertaDemoraEgreso, alertaRuptura, alertasReemplazo y valoresReferencia vienen en data
            // (hermanos de giro), no dentro de giro — confirmado en ACCIONES-FRONT-AMBIGUEDADES.md §4.
            transformResponse: (response: {
                data: {
                    giro: Record<string, any>;
                    alertaDemoraEgreso: AlertaDemoraEgreso;
                    alertaRuptura?: string | null;
                    alertasReemplazo?: string[];
                    valoresReferencia?: ValoresReferenciaGiro | null;
                };
            }): GiroDetalle => ({
                ...response?.data?.giro,
                alertaDemoraEgreso: response?.data?.alertaDemoraEgreso ?? null,
                alertaRuptura: response?.data?.alertaRuptura ?? null,
                alertasReemplazo: response?.data?.alertasReemplazo ?? [],
                valoresReferencia: response?.data?.valoresReferencia ?? null
            }),
            providesTags: (_result, _error, idGiro) => [{ type: 'Giros', id: String(idGiro) }]
        }),
        // AE4-711 — Buques elegibles para reemplazar el buque del giro (Art. 11). Ya filtrados
        // server-side por: tipo crucero, empresa del giro, estado AP/SB, buque actual excluido.
        getBuquesDisponiblesReemplazo: builder.query<BuqueDisponibleReemplazo[], string>({
            query: (idGiro) => ({ url: `giros/${idGiro}/buques-disponibles-reemplazo` }),
            transformResponse: (response: { data: { buques: BuqueDisponibleReemplazo[] } }) =>
                response?.data?.buques ?? [],
            providesTags: (_result, _error, idGiro) => [{ type: 'Giros', id: String(idGiro) }]
        }),
        // AE4-711 — Ejecuta el reemplazo del buque del giro (Art. 11). Sin campo de motivo: lo
        // genera el back.
        reemplazarBuqueGiro: builder.mutation<
            ReemplazarBuqueResponse,
            { idGiro: string; idBuque: number }
        >({
            query: ({ idGiro, idBuque }) => ({
                url: `giros/${idGiro}/buque`,
                method: 'PATCH',
                body: { idBuque }
            }),
            transformResponse: (response: { data: ReemplazarBuqueResponse }) => response?.data,
            invalidatesTags: (_result, _error, { idGiro }) => [
                'Giros',
                { type: 'Giros', id: String(idGiro) },
                { type: 'ModificacionesAgencia', id: String(idGiro) }
            ]
        }),
        answerParametrics: builder.query<any, void>({
            query: (idGiro) => ({
                url: `giros/${idGiro}/conversacion/respuestas`
            }),
            transformResponse: (response: { data: { respuestas: any } }) =>
                response?.data?.respuestas
        }),
        postAnswer: builder.mutation({
            query: ({ idGiro, idRespuesta, data }) => ({
                url: `giros/${idGiro}/conversacion/respuestas/${idRespuesta}`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: (_result, _error, { idGiro }) => ['Giros', { type: 'Giros', id: String(idGiro) }]
        }),
        downloadFile: builder.query({
            query: (idGiro) => ({
                url: `giros/${idGiro}/pdf`
            }),
            transformResponse: (response: { data: { pdf: any } }) => response?.data?.pdf
        }),
        getMovementsHistory: builder.query({
            query: (idGiro) => ({
                url: `giros/${idGiro}/movimientos/historico`
            }),
            transformResponse: (response: { data: { historial: any } }) => response?.data?.historial
        }),
        getTrafficTypes: builder.query({
            query: () => ({
                url: 'giros/tipos-trafico'
            }),
            transformResponse: (response: { data: { tiposTrafico: any } }) =>
                response?.data?.tiposTrafico
        }),
        getOperationTypes: builder.query({
            query: () => ({
                url: 'giros/tipos-operacion'
            }),
            transformResponse: (response: { data: { tiposOperacion: any } }) =>
                response?.data?.tiposOperacion
        }),
        updateTripData: builder.mutation<void, { idGiro: any; data: UpdateTripDataBody }>({
            query: ({ idGiro, data }) => ({
                url: `giros/${idGiro}/linea-vanguardia`,
                method: 'PATCH',
                body: data
            })
        }),
        updateMovement: builder.mutation<void, { idGiro: any; idMove: any; data: { demoraEgresoSinAutorizacion?: boolean; [key: string]: any } }>({
            query: ({ idGiro, idMove, data }) => ({
                url: `giros/${idGiro}/movimientos/${idMove}`,
                method: 'PATCH',
                body: data
            })
        }),
        billingRecord: builder.mutation({
            query: ({ idGiro, formData }) => ({
                url: `giros/${idGiro}/facturacion`,
                method: 'POST',
                body: formData
            })
        }),
        postReviewNote: builder.mutation({
            query: ({ idGiro, body }) => ({
                url: `giros/${idGiro}/revision`,
                body: body,
                method: 'PATCH'
            })
        }),
        finishDockingReview: builder.mutation({
            query: ({ idGiro, data }) => ({
                url: `giros/${idGiro}`,
                method: 'PATCH',
                body: data
            }),
            // AE4-653: el body incluye motivo (obligatorio para giro APROBADO con reserva). excepcionUsd300/motivoExcepcion eliminados.
            invalidatesTags: (_result, _error, { idGiro }) => [
                'Giros',
                { type: 'Giros', id: String(idGiro) },
                { type: 'LiquidacionTasas', id: String(idGiro) }
            ]
        }),
        addNewMov: builder.mutation({
            query: ({ idGiro, data }) => ({
                url: `giros/${idGiro}/movimientos`,
                method: 'POST',
                body: data
            })
        }),
        deleteMove: builder.mutation({
            query: ({ idGiro, idMovement }) => ({
                url: `giros/${idGiro}/movimientos/${idMovement}`,
                method: 'DELETE'
            })
        }),
        renewalDocking: builder.mutation({
            query: ({ idGiro, idMovement }) => ({
                url: `giros/${idGiro}/movimientos/${idMovement}/renovacion`,
                method: 'POST'
            })
        }),
        getDockingRateCodes: builder.query({
            query: () => ({
                url: 'giros/codigos-tarifa'
            }),
            transformResponse: (response: { data: { codigos: any } }) => response?.data?.codigos
        }),
        liquidateDocking: builder.mutation({
            query: (idGiro) => ({
                url: `giros/${idGiro}/liquidacion`,
                method: 'POST'
            })
        }),
        downloadAttachedFile: builder.query({
            query: ({ idGiro }) => ({
                url: `giros/${idGiro}/facturacion`
            }),
            transformResponse: (response: { data: any }) => response?.data
        }),
        downloadPasavantePDF: builder.query({
            query: (idGiro) => ({
                url: `giros/${idGiro}/pasavante/pdf`
            }),
            transformResponse: (response: { data: { pdf: any } }) => response?.data?.pdf
        }),
        anularGiro: builder.mutation({
            query: (idGiro) => ({
                url: `giros/${idGiro}/anular`,
                method: 'PATCH'
            })
        }),
        editGiro: builder.mutation({
            query: ({ idGiro, data, idMovimiento }) => ({
                url: `giros/${idGiro}/movimientos/${idMovimiento}/campos`,
                method: 'PATCH',
                body: data
            }),
            // AE4-709: el body ya no incluye motivo; la justificación del cargo Art. 14° es el campo nota (obligatorio para todos los perfiles). excepcionUsd300/motivoExcepcion eliminados (AE4-653).
            invalidatesTags: (_result, _error, { idGiro }) => [
                'Giros',
                { type: 'Giros', id: String(idGiro) },
                { type: 'LiquidacionTasas', id: String(idGiro) }
            ]
        }),
        getAgendaBookingGiros: builder.query({
            query: (filters: IPayloadBookingGiros) => ({
                url: `giros/booking`,
                params: filters
            }),
            transformResponse: (response: { data: IBookingGirosResponse }) => response?.data?.giros || []
        }),
        getEstadisticasBookingGiros: builder.query({
            query: (seasonId: number) => ({
                url: `giros/estadisticas/temporada/${seasonId}`,
                method: 'GET'
            }),
            transformResponse: (response: { data: IEstadisticasBookingGirosResponse }) => response?.data?.estadisticas
        }),
        getDatosParametricosGiros: builder.query<any, void>({
            query: () => ({ url: 'giros/datos-parametricos' }),
            transformResponse: (response: { data: any }) => response?.data
        }),
        calcularTasas: builder.mutation<any, { idGiro: number | string; body: any }>({
            query: ({ idGiro, body }) => ({
                url: `giros/${idGiro}/tasas/calcular`,
                method: 'POST',
                body
            }),
            invalidatesTags: (_result, _error, { idGiro }) => [
                { type: 'Giros', id: String(idGiro) },
                { type: 'LiquidacionTasas', id: String(idGiro) }
            ],
            transformResponse: (response: { data: any }) => response?.data
        }),
        getTasasVigentes: builder.query<any, number | string>({
            query: (idGiro) => ({ url: `giros/${idGiro}/tasas` }),
            providesTags: (_result, _error, idGiro) => [
                { type: 'LiquidacionTasas', id: String(idGiro) }
            ],
            transformResponse: (response: { data: any }) => response?.data
        }),
        getTasasHistorial: builder.query<any, number | string>({
            query: (idGiro) => ({ url: `giros/${idGiro}/tasas/historial` }),
            providesTags: (_result, _error, idGiro) => [
                { type: 'LiquidacionTasas', id: String(idGiro) }
            ],
            transformResponse: (response: { data: any }) => response?.data
        }),
        // AE4-409 — Preview de penalidad de cancelación (sin persistir). Art. 13 + Art. 14.
        getPreviewCancelacion: builder.query<PreviewPenalidadCancelacion, number | string>({
            query: (idGiro) => ({ url: `giros/${idGiro}/cancelar/preview` }),
            transformResponse: (response: { data: PreviewPenalidadCancelacion }) => response?.data
        }),
        // AE4-409 — Cancelar giro APROBADO con reserva aplicando penalidad. Estado final CANCELADO_CON_PENALIDAD.
        cancelarGiroConPenalidad: builder.mutation<
            CancelarGiroPenalidadResponse,
            { idGiro: number | string; motivo: string }
        >({
            query: ({ idGiro, motivo }) => ({
                url: `giros/${idGiro}/cancelar`,
                method: 'POST',
                body: { motivo }
            }),
            invalidatesTags: (_result, _error, { idGiro }) => [
                'Giros',
                { type: 'Giros', id: String(idGiro) },
                { type: 'LiquidacionTasas', id: String(idGiro) }
            ],
            transformResponse: (response: { data: CancelarGiroPenalidadResponse }) =>
                response?.data
        }),
        // AE4-409 — Upload de documentación de respaldo (fuerza mayor) a S3. Retorna rutaDocumentacion.
        uploadDocEximicion: builder.mutation<
            { rutaDocumentacion: string },
            { idGiro: number | string; file: FormData }
        >({
            query: ({ idGiro, file }) => ({
                url: `giros/${idGiro}/penalidad/eximicion/documento`,
                method: 'POST',
                body: file
            }),
            transformResponse: (response: { data: { rutaDocumentacion: string } }) =>
                response?.data
        }),
        // AE4-409 — Eximir penalidad total/parcial (Admin General). Recibe rutaDocumentacion del upload previo.
        eximirPenalidad: builder.mutation<
            EximirPenalidadResponse,
            { idGiro: number | string; body: EximirPenalidadPayload }
        >({
            query: ({ idGiro, body }) => ({
                url: `giros/${idGiro}/penalidad/eximicion`,
                method: 'POST',
                body
            }),
            invalidatesTags: (_result, _error, { idGiro }) => [
                'Giros',
                { type: 'Giros', id: String(idGiro) },
                { type: 'LiquidacionTasas', id: String(idGiro) }
            ],
            transformResponse: (response: { data: EximirPenalidadResponse }) => response?.data
        }),
        calcularAnticipoReservas: builder.mutation<any, { idGiro: number | string; body: any }>({
            query: ({ idGiro, body }) => ({
                url: `giros/${idGiro}/tasas/calcular-anticipo`,
                method: 'POST',
                body
            }),
            transformResponse: (response: { data: any }) => response?.data
        }),
        // AE4-653 — Listar modificaciones de agencia del giro (modelo diferido Art. 14°)
        getModificacionesAgencia: builder.query<ModificacionesAgenciaResponse, string>({
            query: (idGiro) => ({ url: `giros/${idGiro}/modificaciones-agencia` }),
            providesTags: (_result, _error, idGiro) => [
                { type: 'ModificacionesAgencia', id: String(idGiro) }
            ],
            transformResponse: (response: { data: ModificacionesAgenciaResponse }) => response?.data
        }),
        // AE4-663 — Pre-check de calado al aceptar giro directo (sin reserva) en zonas controladas de USH.
        validarCaladoGiro: builder.query<ValidarCaladoGiroResponse, { idGiro: number | string }>({
            query: ({ idGiro }) => ({ url: `giros/${idGiro}/aceptar/validar-calado` }),
            transformResponse: (response: { data: ValidarCaladoGiroResponse }) => response?.data
        }),
        // AE4-653 — Exceptuar (quitar punibilidad) una modificación de agencia. Solo Admin General
        // (permiso EXCEPCION_CARGO_MODIFICACION = 130 — corregido en AE4-711, ver
        // ACCIONES-FRONT-AMBIGUEDADES.md §3; no es 129 ni 152).
        exceptuarModificacionAgencia: builder.mutation<void, ExceptuarModificacionPayload>({
            query: ({ idGiro, idModificacion, punible, motivo }) => ({
                url: `giros/${idGiro}/modificaciones/${idModificacion}/punible`,
                method: 'PATCH',
                body: { punible, motivo }
            }),
            invalidatesTags: (_result, _error, { idGiro }) => [
                { type: 'ModificacionesAgencia', id: String(idGiro) }
            ]
        }),
        // AE4-710 — Cambia la agencia marítima del giro a su contraparte titular↔subagencia. Permiso CAMBIAR_AGENCIA_GIRO.
        cambiarAgenciaGiro: builder.mutation<CambiarAgenciaGiroResponse, CambiarAgenciaGiroPayload>({
            query: ({ idGiro, idEmpresaNueva }) => ({
                url: `giros/${idGiro}/agencia`,
                method: 'PATCH',
                body: { idEmpresaNueva }
            }),
            transformResponse: (response: { data: CambiarAgenciaGiroResponse }) => response?.data,
            invalidatesTags: (_result, _error, { idGiro }) => [
                'Giros',
                { type: 'Giros', id: String(idGiro) }
            ]
        }),
        // AE4-710 — Agencia elegible para el cambio (contraparte titular↔subagencia del buque). A lo sumo un elemento.
        getAgenciasDisponiblesGiro: builder.query<AgenciaDisponibleGiro[], string>({
            query: (idGiro) => ({ url: `giros/${idGiro}/agencias-disponibles` }),
            transformResponse: (response: { data: { agencias: AgenciaDisponibleGiro[] } }) =>
                response?.data?.agencias ?? [],
            providesTags: (_result, _error, idGiro) => [
                { type: 'Giros', id: `agencias-${idGiro}` }
            ]
        })
    })
});

export const {
    useAnularGiroMutation,
    useLazyDownloadPasavantePDFQuery,
    useLazyDownloadAttachedFileQuery,
    useLiquidateDockingMutation,
    useGetDockingRateCodesQuery,
    useRenewalDockingMutation,
    useDeleteMoveMutation,
    useAddNewMovMutation,
    useFinishDockingReviewMutation,
    usePostReviewNoteMutation,
    useBillingRecordMutation,
    useUpdateMovementMutation,
    useUpdateTripDataMutation,
    useGetOperationTypesQuery,
    useGetTrafficTypesQuery,
    useGetMovementsHistoryQuery,
    useLazyDownloadFileQuery,
    usePostAnswerMutation,
    useLazyAnswerParametricsQuery,
    useLazyGetGiroByIdQuery,
    useLazyHistoryConversationQuery,
    useDockingUploadFilesMutation,
    useAddNewdockingMutation,
    useGetChargeTypesQuery,
    useGetTerminalsQuery,
    useGetPortsQuery,
    useValidateMovsMutation,
    useGetAssemblersAndTenantsQuery,
    useGetGirosQuery,
    useLazyGetBlkDataQuery,
    useLazyDowloadBlkPdfQuery,
    useValidateBlkDataMutation,
    useEditGiroMutation,
    useGetAgendaBookingGirosQuery,
    useGetEstadisticasBookingGirosQuery,
    useGetDatosParametricosGirosQuery,
    useCalcularTasasMutation,
    useGetTasasVigentesQuery,
    useLazyGetTasasVigentesQuery,
    useGetTasasHistorialQuery,
    useLazyGetPreviewCancelacionQuery,
    useCancelarGiroConPenalidadMutation,
    useUploadDocEximicionMutation,
    useEximirPenalidadMutation,
    useCalcularAnticipoReservasMutation,
    useGetModificacionesAgenciaQuery,
    useExceptuarModificacionAgenciaMutation,
    useLazyValidarCaladoGiroQuery,
    useLazyGetBuquesDisponiblesReemplazoQuery,
    useReemplazarBuqueGiroMutation,
    useCambiarAgenciaGiroMutation,
    useLazyGetAgenciasDisponiblesGiroQuery
} = girosApi;
